export interface ExportColumn<TRow> {
  /** Stable key for template persistence. NEVER rename — existing
   *  templates in user localStorage reference these by key. If a column
   *  is dropped, keep the key in a deprecated list or filter it from
   *  old templates at load time. */
  key: string
  header: string
  value: (row: TRow) => unknown
}

/** A leading label/value line written before the header + data rows —
 *  e.g. the report period or the unit a $-column was normalized to.
 *  Consumed by both `rowsToCsv`/`downloadCsv` here and the sibling
 *  `downloadXlsx` in `xlsx.ts`, so a caller writes it once and gets both
 *  formats stamped the same way. */
export interface ExportMetadataEntry {
  label: string
  value: string
}

function esc(v: unknown): string {
  if (v === null || v === undefined) return ''
  const s = String(v)
  if (/[",\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

/**
 * Render rows to CSV. When `columnKeys` is omitted (or empty), every
 * column is included in the file's default order. Otherwise the export
 * honors the provided key list — both subset AND order — so a template
 * like `['customer', 'so_number', 'open']` exports those 3 columns in
 * that order. Unknown keys in `columnKeys` are silently dropped so old
 * templates survive a schema change.
 *
 * `metadata`, when given, is written as one escaped "Label: Value" line
 * per entry, then a blank line, before the header row.
 */
export function rowsToCsv<TRow>(
  rows: readonly TRow[],
  columns: readonly ExportColumn<TRow>[],
  columnKeys?: readonly string[],
  metadata?: readonly ExportMetadataEntry[],
): string {
  const cols =
    columnKeys && columnKeys.length > 0
      ? columnKeys
          .map((k) => columns.find((c) => c.key === k))
          .filter((c): c is ExportColumn<TRow> => Boolean(c))
      : columns
  const header = cols.map((c) => esc(c.header)).join(',')
  const body = rows.map((r) => cols.map((c) => esc(c.value(r))).join(','))
  const metaLines = metadata?.length
    ? [...metadata.map((m) => esc(`${m.label}: ${m.value}`)), '']
    : []
  return [...metaLines, header, ...body].join('\n')
}

export interface DownloadCsvOptions {
  filenameBase: string
  columnKeys?: readonly string[]
  metadata?: readonly ExportMetadataEntry[]
}

export function downloadCsv<TRow>(
  rows: readonly TRow[],
  columns: readonly ExportColumn<TRow>[],
  opts: DownloadCsvOptions,
): void {
  const csv = rowsToCsv(rows, columns, opts.columnKeys, opts.metadata)
  const today = new Date().toISOString().slice(0, 10)
  const name = `${opts.filenameBase}-${today}.csv`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
